import { CustomEvents } from "../../config/CustomEvent";
import { PersistenceLevel } from "../../config/PersistenceLevels";
import BaseEntity from "../../core/entity/BaseEntity";
import Entity from "../../core/entity/Entity";
import MusicGenerator, { MusicParams } from "./MusicGenerator";

/**
 * Controls music parameters based on game state transitions.
 *
 * Listens for game events (level start, level complete, game over, etc.)
 * and applies appropriate music parameters to create a dynamic soundtrack
 * that responds to gameplay. Each game state has predefined tempo and
 * intensity values.
 *
 * State transitions:
 * - Menu: Calm, moderate tempo (120 BPM)
 * - Playing: High energy, fast tempo (150 BPM)
 * - Upgrade: Slower, reflective feel (100 BPM)
 * - Game Over: Somber mood (90 BPM)
 *
 * Uses smooth tempo transitions to avoid jarring changes.
 */
export default class MusicController extends BaseEntity implements Entity {
  tags = ["musicController"];
  id = "musicController";
  pausable = false; // Keep music control active during pause
  persistenceLevel = PersistenceLevel.Permanent;

  /** Music parameters for main menu state */
  private static readonly MENU_PARAMS: MusicParams = {
    tempo: 120, // Moderate, welcoming pace
    intensity: 0.5, // Medium energy
  };

  /** Music parameters for active gameplay */
  private static readonly PLAYING_PARAMS: MusicParams = {
    tempo: 150, // Fast-paced for action
    intensity: 0.8, // High energy
  };

  /** Music parameters for upgrade/victory state */
  private static readonly UPGRADE_PARAMS: MusicParams = {
    tempo: 135, // Slower, more contemplative
    intensity: 0.3, // Low energy, reflective
  };

  /** Music parameters for game over state */
  private static readonly GAME_OVER_PARAMS: MusicParams = {
    tempo: 90, // Slow, somber pace
    intensity: 0.5, // Medium intensity
  };

  /**
   * Get the MusicGenerator entity
   */
  get musicGenerator(): MusicGenerator | undefined {
    const maybeMusicGenerator = this.game?.entities.getById("musicGenerator");
    if (maybeMusicGenerator instanceof MusicGenerator) {
      return maybeMusicGenerator;
    }
    return undefined;
  }

  onAdd(): void {
    // Initialize with menu tempo
    this.setMusicParams(MusicController.MENU_PARAMS);
  }

  /**
   * When a level starts, switch to playing mode
   */
  onLevelStart({ level }: CustomEvents["levelStart"]): void {
    this.setMusicParams(MusicController.PLAYING_PARAMS);
  }

  /**
   * When a level completes, switch to upgrade mode
   */
  onLevelComplete({
    level,
    enemiesKilled,
  }: CustomEvents["levelComplete"]): void {
    this.setMusicParams(MusicController.UPGRADE_PARAMS);
  }

  /**
   * Game over - switch to game over mode
   */
  onGameOver({}): void {
    this.setMusicParams(MusicController.GAME_OVER_PARAMS);
  }

  /**
   * When an upgrade is chosen, return to playing mode
   */
  onUpgradeChosen({ upgrade }: CustomEvents["upgradeChosen"]): void {
    // Note: This will trigger when the next level starts via onLevelStart
    // For now, we could stay in upgrade mode until the next level
  }

  /**
   * When returning to main menu, switch to menu mode
   */
  onMenuStart({}): void {
    this.setMusicParams(MusicController.MENU_PARAMS);
  }

  /**
   * Apply new music parameters with smooth tempo transitions.
   * Non-tempo parameters are applied immediately while tempo
   * is ramped over 2 beats to avoid jarring changes.
   */
  private setMusicParams(targetParams: MusicParams): void {
    const musicGen = this.musicGenerator;
    if (!musicGen) return;

    // Apply smooth tempo transition (2 beats duration)
    musicGen.requestTempoRamp(
      musicGen.getCurrentTempo(),
      targetParams.tempo,
      2,
    );

    // Update music intensity parameter
    musicGen.setParams({
      intensity: targetParams.intensity,
    });
  }
}
